import {
  Archive,
  ClipboardList,
  Copy,
  FileQuestion,
  Pencil,
  Plus,
  Shield,
  Star,
  Trash2,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import useLocale from '../contexts/LocaleContext';
import { useFacilityTypes } from '../hooks/useFacilities';
import { questionsApi } from '../lib/api-client';
import { hasPermission } from '../lib/permissions';
import { getCurrentUser } from '../hooks/useAuth';
import type {
  HygieneQuestionnaireListItemDto,
  ComplianceQuestionnaireListItemDto,
  CreateQuestionnaireDto,
  CreateComplianceQuestionnaireDto,
  CopyQuestionnaireDto,
} from '../types/api';

type TabId = 'hygiene' | 'compliance';

function Modal({ title, children, onClose, open }: { title: string; children: React.ReactNode; onClose: () => void; open: boolean }) {
  if (!open) return null;
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50' onClick={onClose}>
      <div className='bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col' onClick={e => e.stopPropagation()}>
        <div className='px-4 py-3 border-b border-gray-200 font-semibold text-gray-900'>{title}</div>
        <div className='p-4 overflow-y-auto flex-1'>{children}</div>
      </div>
    </div>
  );
}

function QuestionnaireCard({
  name,
  description,
  isCurrent,
  questionCount,
  editPath,
  facilityTypeName,
  onCopy,
  onPromote,
  onArchive,
  onDelete,
  canPromote,
  canArchive,
  canCopy,
  canEdit,
  canDelete,
}: {
  name: string;
  description?: string;
  isCurrent: boolean;
  questionCount: number;
  editPath: string;
  facilityTypeName?: string;
  onCopy: () => void;
  onPromote?: () => void;
  onArchive?: () => void;
  onDelete: () => void;
  canPromote: boolean;
  canArchive: boolean;
  canCopy: boolean;
  canEdit: boolean;
  canDelete: boolean;
}) {
  return (
    <div className={`flex items-start justify-between gap-3 p-4 rounded-lg border transition-colors ${
      isCurrent
        ? 'border-green-200 bg-green-50/40'
        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
    }`}>
      <Link to={editPath} className='min-w-0 flex-1 group'>
        <div className='flex items-center gap-2 flex-wrap'>
          <span className='font-medium text-gray-900 group-hover:text-primary truncate'>{name}</span>
          {isCurrent && (
            <span className='flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-green-100 text-green-800 text-xs font-medium'>
              <Star className='h-3 w-3' /> Current
            </span>
          )}
        </div>
        {description && (
          <div className='text-xs text-gray-500 mt-0.5 truncate'>{description}</div>
        )}
        <div className='flex items-center gap-3 mt-1.5 text-xs text-gray-500'>
          <span>{questionCount} questions</span>
          {facilityTypeName && (
            <span className='px-1.5 py-0.5 rounded bg-secondary text-primary font-medium'>{facilityTypeName}</span>
          )}
        </div>
      </Link>

      <div className='flex items-center gap-1 flex-shrink-0'>
        {canPromote && !isCurrent && onPromote && (
          <button
            onClick={onPromote}
            className='flex items-center gap-1 px-2 py-1 text-xs text-green-700 hover:bg-green-50 border border-green-200 rounded-md transition-colors'
            title='Set as current'
          >
            <Star className='h-3 w-3' /> Set current
          </button>
        )}
        {canArchive && isCurrent && onArchive && (
          <button
            onClick={onArchive}
            className='flex items-center gap-1 px-2 py-1 text-xs text-amber-700 hover:bg-amber-50 border border-amber-200 rounded-md transition-colors'
            title='Archive (set as not current)'
          >
            <Archive className='h-3 w-3' /> Archive
          </button>
        )}
        {canCopy && (
          <button
            onClick={onCopy}
            className='p-1.5 text-gray-500 hover:text-primary hover:bg-secondary rounded-md transition-colors'
            title='Copy to another facility type'
          >
            <Copy className='h-4 w-4' />
          </button>
        )}
        {canEdit && (
          <Link
            to={editPath}
            className='p-1.5 text-gray-500 hover:text-primary hover:bg-secondary rounded-md transition-colors'
            title='Edit'
          >
            <Pencil className='h-4 w-4' />
          </Link>
        )}
        {canDelete && (
          <button
            onClick={onDelete}
            className='p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors'
            title='Delete'
          >
            <Trash2 className='h-4 w-4' />
          </button>
        )}
      </div>
    </div>
  );
}

export default function QuestionManagement() {
  const { dictionary } = useLocale();
  const user = getCurrentUser();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('hygiene');
  const [facilityTypeFilter, setFacilityTypeFilter] = useState<string>('');
  const [complianceFacilityTypeId, setComplianceFacilityTypeId] = useState<string>('');
  const [createCompQuestionnaireOpen, setCreateCompQuestionnaireOpen] = useState(false);
  const [createQuestionnaireOpen, setCreateQuestionnaireOpen] = useState(false);
  const [copyTarget, setCopyTarget] = useState<{ id: string; name: string } | null>(null);

  const { data: facilityTypes = [] } = useFacilityTypes();

  const facilityTypeMap = Object.fromEntries(facilityTypes.map(ft => [ft.id, ft.name]));

  // ─── Hygiene mutations ────────────────────────────────────────────────────
  const createQuestionnaireMutation = useMutation({
    mutationFn: (body: CreateQuestionnaireDto) => questionsApi.createHygieneQuestionnaire(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', 'hygiene'] });
      setCreateQuestionnaireOpen(false);
      toast.success(dictionary['questions.questionnaire_created'] ?? 'Questionnaire created');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const promoteHygieneQuestionnaireMutation = useMutation({
    mutationFn: (questionnaireId: string) =>
      questionsApi.updateHygieneQuestionnaire(questionnaireId, { isCurrent: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', 'hygiene', 'questionnaires'] });
      toast.success('Questionnaire set as current');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const archiveHygieneQuestionnaireMutation = useMutation({
    mutationFn: (questionnaireId: string) =>
      questionsApi.updateHygieneQuestionnaire(questionnaireId, { isCurrent: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', 'hygiene', 'questionnaires'] });
      toast.success('Questionnaire archived');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const deleteHygieneQuestionnaireMutation = useMutation({
    mutationFn: (questionnaireId: string) => questionsApi.deleteHygieneQuestionnaire(questionnaireId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', 'hygiene', 'questionnaires'] });
      toast.success(dictionary['questions.deleted'] ?? 'Deleted');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  // ─── Compliance mutations ─────────────────────────────────────────────────
  const createCompQuestionnaireMutation = useMutation({
    mutationFn: (body: CreateComplianceQuestionnaireDto) => questionsApi.createComplianceQuestionnaire(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', 'compliance', 'questionnaires'] });
      setCreateCompQuestionnaireOpen(false);
      toast.success('Compliance questionnaire created');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const promoteCompQuestionnaireMutation = useMutation({
    mutationFn: (questionnaireId: string) =>
      questionsApi.updateComplianceQuestionnaire(questionnaireId, { isCurrent: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', 'compliance', 'questionnaires'] });
      toast.success('Questionnaire set as current');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const archiveCompQuestionnaireMutation = useMutation({
    mutationFn: (questionnaireId: string) =>
      questionsApi.updateComplianceQuestionnaire(questionnaireId, { isCurrent: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', 'compliance', 'questionnaires'] });
      toast.success('Questionnaire archived');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const deleteCompQuestionnaireMutation = useMutation({
    mutationFn: (questionnaireId: string) => questionsApi.deleteComplianceQuestionnaire(questionnaireId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', 'compliance', 'questionnaires'] });
      toast.success('Deleted');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const copyQuestionnaireMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CopyQuestionnaireDto }) =>
      questionsApi.copyQuestionnaire(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      setCopyTarget(null);
      toast.success('Questionnaire copied successfully');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to copy'),
  });

  const canRead = hasPermission(user as any, 'QUESTIONNAIRES_READ');
  const canCreate = hasPermission(user as any, 'QUESTIONNAIRES_CREATE');
  const canUpdate = hasPermission(user as any, 'QUESTIONNAIRES_UPDATE');
  const canDelete = hasPermission(user as any, 'QUESTIONNAIRES_DELETE');

  // ─── Queries ──────────────────────────────────────────────────────────────
  const { data: hygieneQuestionnaires = [], isLoading: hygieneLoading } = useQuery({
    queryKey: ['questions', 'hygiene', 'questionnaires', facilityTypeFilter || undefined],
    queryFn: () => questionsApi.listHygieneQuestionnaires(facilityTypeFilter || undefined),
    enabled: canRead,
  });

  const { data: complianceQuestionnaires = [], isLoading: complianceLoading } = useQuery({
    queryKey: ['questions', 'compliance', 'questionnaires', complianceFacilityTypeId || undefined],
    queryFn: () => questionsApi.listComplianceQuestionnaires(complianceFacilityTypeId || undefined),
    enabled: canRead,
  });

  if (!canRead) {
    return (
      <div className='p-4 sm:p-6 max-w-4xl mx-auto'>
        <div className='bg-amber-50 border border-amber-200 rounded-lg p-6 text-center'>
          <Shield className='h-12 w-12 text-amber-600 mx-auto mb-3' />
          <h2 className='text-lg font-semibold text-amber-900'>
            {dictionary['nav.question_management'] ?? 'Question Management'}
          </h2>
          <p className='text-sm text-amber-800 mt-1'>
            This section is only available to administrators.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'hygiene' as TabId, label: dictionary['questions.hygiene'] ?? 'Hygiene questionnaires', icon: FileQuestion },
    { id: 'compliance' as TabId, label: dictionary['questions.compliance'] ?? 'Compliance questions', icon: ClipboardList },
  ];

  return (
    <div className='p-4 sm:p-6 mx-auto space-y-6'>
      <div>
        <h1 className='text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2'>
          <ClipboardList className='h-6 w-6 sm:h-7 sm:w-7 text-primary' />
          {dictionary['nav.question_management'] ?? 'Question Management'}
        </h1>
        <p className='text-sm text-gray-600 mt-1'>
          {dictionary['questions.description'] ?? 'Manage hygiene questionnaire questions and compliance questions (Admin only).'}
        </p>
      </div>

      <div className='border-b border-gray-200'>
        <nav className='flex gap-1' aria-label='Tabs'>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-secondary'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className='h-4 w-4' />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'hygiene' && (
        <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'>
            <h2 className='text-base font-semibold text-gray-900'>
              {dictionary['questions.hygiene_questionnaires'] ?? 'Hygiene questionnaires'}
            </h2>
            <div className='flex items-center gap-2 flex-wrap'>
              {canCreate && (
                <Button size='sm' onClick={() => setCreateQuestionnaireOpen(true)}>
                  <Plus className='h-4 w-4 mr-1' />{dictionary['questions.create_questionnaire'] ?? 'Create questionnaire'}
                </Button>
              )}
              <label className='text-xs font-medium text-gray-600 whitespace-nowrap'>{dictionary['questions.facility_type'] ?? 'Facility type'}</label>
              <select
                value={facilityTypeFilter}
                onChange={e => setFacilityTypeFilter(e.target.value)}
                className='text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white min-w-[160px]'
              >
                <option value=''>{dictionary['common.all_types'] ?? 'All'}</option>
                {facilityTypes.map(ft => (
                  <option key={ft.id} value={ft.id}>{ft.name}</option>
                ))}
              </select>
            </div>
          </div>
          {hygieneLoading ? (
            <div className='flex justify-center py-12'>
              <div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' />
            </div>
          ) : hygieneQuestionnaires.length === 0 ? (
            <div className='text-center py-12 text-gray-500 text-sm'>
              {dictionary['questions.no_questionnaires'] ?? 'No questionnaires found.'}
            </div>
          ) : (
            <ul className='space-y-2'>
              {hygieneQuestionnaires.map((q: HygieneQuestionnaireListItemDto) => (
                <li key={q.id}>
                  <QuestionnaireCard
                    name={q.name}
                    description={q.description}
                    isCurrent={q.isCurrent}
                    questionCount={q.questionCount}
                    editPath={`/questions/hygiene/${q.id}`}
                    facilityTypeName={q.facilityTypeId ? facilityTypeMap[q.facilityTypeId] : undefined}
                    onCopy={() => setCopyTarget({ id: q.id, name: q.name })}
                    onPromote={() => {
                      if (window.confirm('Set this questionnaire as current? The existing current one will be demoted.'))
                        promoteHygieneQuestionnaireMutation.mutate(q.id);
                    }}
                    onArchive={() => {
                      if (window.confirm('Archive this questionnaire? It will no longer be the current one.'))
                        archiveHygieneQuestionnaireMutation.mutate(q.id);
                    }}
                    onDelete={() => {
                      if (window.confirm(dictionary['questions.confirm_delete_question'] ?? 'Delete this questionnaire?'))
                        deleteHygieneQuestionnaireMutation.mutate(q.id);
                    }}
                    canPromote={canUpdate}
                    canArchive={canUpdate}
                    canCopy={canCreate}
                    canEdit={canUpdate}
                    canDelete={canDelete}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {activeTab === 'compliance' && (
        <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'>
            <h2 className='text-base font-semibold text-gray-900'>
              {dictionary['questions.compliance_questionnaires'] ?? 'Compliance questionnaires'}
            </h2>
            <div className='flex items-center gap-2 flex-wrap'>
              {canCreate && (
                <Button size='sm' onClick={() => setCreateCompQuestionnaireOpen(true)}>
                  <Plus className='h-4 w-4 mr-1' />{dictionary['questions.create_questionnaire'] ?? 'Create questionnaire'}
                </Button>
              )}
              <label className='text-xs font-medium text-gray-600 whitespace-nowrap'>{dictionary['questions.facility_type'] ?? 'Facility type'}</label>
              <select
                value={complianceFacilityTypeId}
                onChange={e => setComplianceFacilityTypeId(e.target.value)}
                className='text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white min-w-[160px]'
              >
                <option value=''>{dictionary['common.all_types'] ?? 'All'}</option>
                {facilityTypes.map(ft => (
                  <option key={ft.id} value={ft.id}>{ft.name}</option>
                ))}
              </select>
            </div>
          </div>
          {complianceLoading ? (
            <div className='flex justify-center py-12'>
              <div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' />
            </div>
          ) : complianceQuestionnaires.length === 0 ? (
            <div className='text-center py-12 text-gray-500 text-sm'>
              {dictionary['questions.no_questionnaires'] ?? 'No questionnaires found.'}
            </div>
          ) : (
            <ul className='space-y-2'>
              {complianceQuestionnaires.map((q: ComplianceQuestionnaireListItemDto) => (
                <li key={q.id}>
                  <QuestionnaireCard
                    name={q.name}
                    description={q.description}
                    isCurrent={q.isCurrent}
                    questionCount={q.questionCount}
                    editPath={`/questions/compliance/${q.id}`}
                    facilityTypeName={q.facilityTypeId ? facilityTypeMap[q.facilityTypeId] : undefined}
                    onCopy={() => setCopyTarget({ id: q.id, name: q.name })}
                    onPromote={() => {
                      if (window.confirm('Set this questionnaire as current? The existing current one will be demoted.'))
                        promoteCompQuestionnaireMutation.mutate(q.id);
                    }}
                    onArchive={() => {
                      if (window.confirm('Archive this questionnaire? It will no longer be the current one.'))
                        archiveCompQuestionnaireMutation.mutate(q.id);
                    }}
                    onDelete={() => {
                      if (window.confirm(dictionary['questions.confirm_delete_question'] ?? 'Delete this questionnaire?'))
                        deleteCompQuestionnaireMutation.mutate(q.id);
                    }}
                    canPromote={canUpdate}
                    canArchive={canUpdate}
                    canCopy={canCreate}
                    canEdit={canUpdate}
                    canDelete={canDelete}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {createQuestionnaireOpen && (
        <CreateQuestionnaireModal
          facilityTypes={facilityTypes}
          onClose={() => setCreateQuestionnaireOpen(false)}
          onSubmit={(body) => createQuestionnaireMutation.mutate(body)}
          isPending={createQuestionnaireMutation.isPending}
          dictionary={dictionary}
        />
      )}

      {createCompQuestionnaireOpen && (
        <CreateComplianceQuestionnaireModal
          facilityTypes={facilityTypes}
          initialFacilityTypeId={complianceFacilityTypeId}
          onClose={() => setCreateCompQuestionnaireOpen(false)}
          onSubmit={(body) => createCompQuestionnaireMutation.mutate(body)}
          isPending={createCompQuestionnaireMutation.isPending}
          dictionary={dictionary}
        />
      )}

      {copyTarget && (
        <CopyQuestionnaireModal
          sourceName={copyTarget.name}
          facilityTypes={facilityTypes}
          onClose={() => setCopyTarget(null)}
          onSubmit={(body) => copyQuestionnaireMutation.mutate({ id: copyTarget.id, body })}
          isPending={copyQuestionnaireMutation.isPending}
        />
      )}
    </div>
  );
}

function CreateQuestionnaireModal({
  facilityTypes,
  onClose,
  onSubmit,
  isPending,
  dictionary,
}: {
  facilityTypes: { id: string; name: string }[];
  onClose: () => void;
  onSubmit: (body: CreateQuestionnaireDto) => void;
  isPending: boolean;
  dictionary: Record<string, string>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [facilityTypeId, setFacilityTypeId] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim() || undefined, facilityTypeId: facilityTypeId || undefined });
  };
  return (
    <Modal title={dictionary['questions.create_questionnaire'] ?? 'Create questionnaire'} open onClose={onClose}>
      <form onSubmit={handleSubmit} className='space-y-3'>
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['questions.name'] ?? 'Name'}</label>
          <Input value={name} onChange={e => setName(e.target.value)} required placeholder='e.g. Hygiene 2025' />
        </div>
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['questions.description'] ?? 'Description'}</label>
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder='Optional' />
        </div>
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['questions.facility_type'] ?? 'Facility type'}</label>
          <select
            value={facilityTypeId}
            onChange={e => setFacilityTypeId(e.target.value)}
            className='w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white'
          >
            <option value=''>{dictionary['common.all_types'] ?? 'All'}</option>
            {facilityTypes.map(ft => <option key={ft.id} value={ft.id}>{ft.name}</option>)}
          </select>
        </div>
        <div className='flex gap-2 pt-2'>
          <Button type='submit' disabled={isPending}>{isPending ? (dictionary['common.saving'] ?? 'Saving…') : (dictionary['common.save'] ?? 'Save')}</Button>
          <Button type='button' variant='outline' onClick={onClose}>{dictionary['common.cancel'] ?? 'Cancel'}</Button>
        </div>
      </form>
    </Modal>
  );
}

function CreateComplianceQuestionnaireModal({
  facilityTypes,
  initialFacilityTypeId,
  onClose,
  onSubmit,
  isPending,
  dictionary,
}: {
  facilityTypes: { id: string; name: string }[];
  initialFacilityTypeId: string;
  onClose: () => void;
  onSubmit: (body: CreateComplianceQuestionnaireDto) => void;
  isPending: boolean;
  dictionary: Record<string, string>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [facilityTypeId, setFacilityTypeId] = useState(initialFacilityTypeId);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim() || undefined, facilityTypeId: facilityTypeId || undefined });
  };
  return (
    <Modal title={dictionary['questions.create_questionnaire'] ?? 'Create questionnaire'} open onClose={onClose}>
      <form onSubmit={handleSubmit} className='space-y-3'>
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['questions.name'] ?? 'Name'}</label>
          <Input value={name} onChange={e => setName(e.target.value)} required placeholder='e.g. Compliance Q1 2025' />
        </div>
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['questions.description'] ?? 'Description'}</label>
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder='Optional' />
        </div>
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['questions.facility_type'] ?? 'Facility type'}</label>
          <select
            value={facilityTypeId}
            onChange={e => setFacilityTypeId(e.target.value)}
            className='w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white'
          >
            <option value=''>{dictionary['common.all_types'] ?? 'All'}</option>
            {facilityTypes.map(ft => <option key={ft.id} value={ft.id}>{ft.name}</option>)}
          </select>
        </div>
        <div className='flex gap-2 pt-2'>
          <Button type='submit' disabled={isPending}>{isPending ? (dictionary['common.saving'] ?? 'Saving…') : (dictionary['common.save'] ?? 'Save')}</Button>
          <Button type='button' variant='outline' onClick={onClose}>{dictionary['common.cancel'] ?? 'Cancel'}</Button>
        </div>
      </form>
    </Modal>
  );
}

function CopyQuestionnaireModal({
  sourceName,
  facilityTypes,
  onClose,
  onSubmit,
  isPending,
}: {
  sourceName: string;
  facilityTypes: { id: string; name: string }[];
  onClose: () => void;
  onSubmit: (body: CopyQuestionnaireDto) => void;
  isPending: boolean;
}) {
  const [facilityTypeId, setFacilityTypeId] = useState('');
  const [customName, setCustomName] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!facilityTypeId) return;
    onSubmit({ facilityTypeId, name: customName.trim() || undefined });
  };
  return (
    <Modal title='Copy questionnaire' open onClose={onClose}>
      <div className='mb-4 p-3 bg-secondary border border-primary/20 rounded-lg text-sm text-primary'>
        <p className='font-medium'>Copying: <span className='font-semibold'>{sourceName}</span></p>
        <p className='mt-1 text-xs text-primary'>
          All sections, questions, sub-questions, and options will be deep-cloned to the selected facility type.
          The copy will be set as current for that facility type.
        </p>
      </div>
      <form onSubmit={handleSubmit} className='space-y-3'>
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>Target facility type <span className='text-red-500'>*</span></label>
          <select
            value={facilityTypeId}
            onChange={e => setFacilityTypeId(e.target.value)}
            required
            className='w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white'
          >
            <option value=''>Select facility type…</option>
            {facilityTypes.map(ft => <option key={ft.id} value={ft.id}>{ft.name}</option>)}
          </select>
        </div>
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>Custom name <span className='text-gray-400'>(optional)</span></label>
          <Input
            value={customName}
            onChange={e => setCustomName(e.target.value)}
            placeholder={`${sourceName} (Copy)`}
          />
        </div>
        <div className='flex gap-2 pt-2'>
          <Button type='submit' disabled={isPending || !facilityTypeId}>
            <Copy className='h-4 w-4 mr-1.5' />
            {isPending ? 'Copying…' : 'Copy questionnaire'}
          </Button>
          <Button type='button' variant='outline' onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}
